import type * as React from "react";
import { ReactRenderer } from "@tiptap/react";
import type {
  SuggestionProps,
  SuggestionKeyDownProps,
  SuggestionOptions,
} from "@tiptap/suggestion";
import tippy, { type Instance as TippyInstance } from "tippy.js";

/** Imperative handle every chip popup list component must expose. */
export interface ChipListHandle {
  onKeyDown: (event: KeyboardEvent) => boolean;
}

export interface ChipSuggestionConfig<Item, Props extends object> {
  /** Trigger sequence, e.g. `"@"` or `"{{"`. */
  char: string;
  /** Item source for the current query. Errors resolve to an empty list. */
  items: (query: string) => Item[] | Promise<Item[]>;
  /** Popup list component; must forward a {@link ChipListHandle} ref. */
  component: React.ForwardRefExoticComponent<Props & React.RefAttributes<ChipListHandle>>;
  /** Maps the live suggestion props onto the popup component's props. */
  componentProps: (props: SuggestionProps<Item>) => Props;
  /**
   * Let the query span spaces, so a multi-word entity name (`@Call Details`)
   * stays inside one suggestion session. Off by default: Tiptap's
   * space-allowing matcher runs the match to the end of the line, so an
   * abandoned `@` would otherwise hold the popup open over the rest of the
   * paragraph. Surfaces that opt in get {@link SUGGESTION_QUERY_MAX} as the
   * backstop (see `shouldShow` below); the `{{variable}}` surface, whose
   * tokens never contain spaces, stays on the default.
   */
  allowSpaces?: boolean;
}

/**
 * Longest query a space-allowing surface keeps the popup open for. Generous
 * enough for the longest reference anything can offer (a 120-char custom-model
 * name plus `.` plus a 64-char field); past it the `@` is prose, not a name.
 */
const SUGGESTION_QUERY_MAX = 192;

/**
 * Builds a Tiptap suggestion config that renders a React popup list in a tippy
 * popover anchored at the trigger. Shared by every chip editor surface (the
 * AOP `@` popup, the task-template `{{` variable popup); only the trigger
 * char, item source, and list component differ per surface.
 */
export function createChipSuggestion<Item, Props extends object>(
  config: ChipSuggestionConfig<Item, Props>,
): Omit<SuggestionOptions<Item>, "editor"> {
  return {
    char: config.char,
    ...(config.allowSpaces
      ? {
          allowSpaces: true,
          shouldShow: ({ query }: { query: string }) => query.length <= SUGGESTION_QUERY_MAX,
        }
      : {}),
    async items({ query }: { query: string }) {
      try {
        return await config.items(query);
      } catch {
        return [];
      }
    },
    render() {
      let component: ReactRenderer | null = null;
      let popup: TippyInstance[] = [];

      return {
        onStart(props: SuggestionProps<Item>) {
          component = new ReactRenderer(config.component, {
            props: config.componentProps(props),
            editor: props.editor,
          });
          if (!props.clientRect) return;
          popup = tippy("body", {
            getReferenceClientRect: props.clientRect as () => DOMRect,
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: "manual",
            placement: "bottom-start",
            touch: true,
          });
        },
        onUpdate(props: SuggestionProps<Item>) {
          component?.updateProps(config.componentProps(props));
          if (props.clientRect) {
            popup[0]?.setProps({ getReferenceClientRect: props.clientRect as () => DOMRect });
          }
        },
        onKeyDown(props: SuggestionKeyDownProps) {
          if (props.event.key === "Escape") {
            popup[0]?.hide();
            return true;
          }
          return (component?.ref as ChipListHandle | null)?.onKeyDown(props.event) ?? false;
        },
        onExit() {
          popup[0]?.destroy();
          component?.destroy();
        },
      };
    },
  };
}
